<template>
  <div class="my-shifts-view">
    <div class="pt-3 pb-3">
      <VContainer>
        <VLayout row wrap justify-center>
          <VFlex xs12 sm10 md8 style="max-width: 500px;">
            <!-- Back button -->
            <BaseButton
              depressed
              color="grey darken-1"
              small
              md-icon="arrow_left"
              md-icon-position="left"
              class="mb-2 ml-0"
              @click="handleReset"
            >
              Back
            </BaseButton>

            <!-- Loading state in header -->
            <template v-if="!isLoading">
              <!-- Greeting -->
              <p class="greeting white--text text-xs-center mb-0">
                Hi {{ laborerFirstName }}.
              </p>
              <p class="text-xs-center mb-2">
                <a href="#" class="not-you-link" @click.prevent="handleReset"
                  >Not you?</a
                >
              </p>

              <!-- Day navigation links -->
              <VLayout row wrap justify-center class="day-nav">
                <VBtn
                  large
                  :outline="selectedDay !== 'yesterday'"
                  :color="selectedDay === 'yesterday' ? 'grey darken-1' : ''"
                  :dark="selectedDay === 'yesterday'"
                  :loading="loadingDay === 'yesterday'"
                  :class="[
                    'mx-1',
                    { 'white--text': selectedDay !== 'yesterday' },
                  ]"
                  @click="selectDay('yesterday')"
                >
                  Yesterday
                </VBtn>
                <VBtn
                  large
                  :outline="selectedDay !== 'today'"
                  :color="selectedDay === 'today' ? 'blue darken-1' : ''"
                  :dark="selectedDay === 'today'"
                  :loading="loadingDay === 'today'"
                  :class="['mx-1', { 'white--text': selectedDay !== 'today' }]"
                  @click="selectDay('today')"
                >
                  Today
                </VBtn>
                <VBtn
                  large
                  :outline="selectedDay !== 'tomorrow'"
                  :color="selectedDay === 'tomorrow' ? 'orange darken-1' : ''"
                  :dark="selectedDay === 'tomorrow'"
                  :loading="loadingDay === 'tomorrow'"
                  :class="[
                    'mx-1',
                    { 'white--text': selectedDay !== 'tomorrow' },
                  ]"
                  @click="selectDay('tomorrow')"
                >
                  Tomorrow
                </VBtn>
              </VLayout>
            </template>
          </VFlex>
        </VLayout>
      </VContainer>
    </div>

    <!-- Scrollable content -->
    <VContainer class="content-area">
      <VLayout row wrap justify-center>
        <VFlex xs12 sm10 md8 style="max-width: 500px;">
          <!-- Initial loading state -->
          <VCard v-if="isLoading" class="elevation-4 pa-4 text-xs-center">
            <VProgressCircular
              indeterminate
              color="orange darken-1"
              size="50"
              class="my-4"
            ></VProgressCircular>
            <p class="mt-2">Loading your shifts...</p>
          </VCard>

          <template v-else>
            <!-- Shift Display with transition -->
            <transition :name="transitionName" mode="out-in">
              <!-- Tomorrow not yet available message -->
              <VCard
                v-if="selectedDay === 'tomorrow' && !isAfter7pmEDT"
                :key="`tomorrow-not-available`"
                class="elevation-4 pa-4 text-xs-center"
              >
                <VIcon color="orange darken-1" size="48" class="mb-3"
                  >schedule</VIcon
                >
                <p class="headline mb-2">Tomorrow's Shifts</p>
                <p class="body-1 grey--text text--darken-1">
                  Please check back after 7pm EDT for tomorrow's shift details.
                </p>
              </VCard>

              <div v-else-if="currentShifts.length" :key="selectedDay">
                <ShiftDisplay
                  v-for="(shift, index) in currentShifts"
                  :key="
                    `${shift.reportDate}-${shift.locationIdentifier}-${index}`
                  "
                  :shift="shift"
                  :day-label="dayLabel"
                  :color="dayColor"
                  :class="{ 'mt-3': index > 0 }"
                />
              </div>

              <!-- No Shifts Message -->
              <NoShiftMessage
                v-else
                :key="`no-shift-${selectedDay}`"
                :color="dayColor"
              />
            </transition>
          </template>
        </VFlex>
      </VLayout>
    </VContainer>
  </div>
</template>

<script>
import { VContainer, VLayout, VFlex } from 'vuetify/lib'
import { VCard } from '@vuetify/VCard'
import VBtn from '@vuetify/VBtn'
import VIcon from '@vuetify/VIcon'
import VProgressCircular from '@vuetify/VProgressCircular'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import lambdaApi from '@services/lambdaApi'

import ShiftDisplay from '@components/MyShifts/ShiftDisplay.vue'
import NoShiftMessage from '@components/MyShifts/NoShiftMessage.vue'

dayjs.extend(utc)
dayjs.extend(timezone)

const DAY_ORDER = ['yesterday', 'today', 'tomorrow']

export default {
  name: 'MyShiftDetailsView',
  components: {
    VContainer,
    VLayout,
    VFlex,
    VCard,
    VBtn,
    VIcon,
    VProgressCircular,
    ShiftDisplay,
    NoShiftMessage,
  },
  data() {
    return {
      shifts: {
        yesterday: null, // null = not loaded, [] = loaded but empty
        today: null,
        tomorrow: null,
      },
      selectedDay: 'tomorrow',
      laborerId: null,
      laborerFirstName: '',
      isLoading: false,
      loadingDay: null,
      transitionName: 'slide-right',
    }
  },
  computed: {
    isAfter7pmEDT() {
      return (
        dayjs()
          .tz('America/New_York')
          .hour() >= 19
      )
    },
    currentShifts() {
      return this.shifts[this.selectedDay] || []
    },
    dayLabel() {
      const labels = {
        yesterday: 'Yesterday',
        today: 'Today',
        tomorrow: 'Tomorrow',
      }
      return labels[this.selectedDay] || ''
    },
    dayColor() {
      const colors = {
        yesterday: 'grey darken-1',
        today: 'blue darken-1',
        tomorrow: 'orange darken-1',
      }
      return colors[this.selectedDay] || 'orange darken-1'
    },
  },
  async created() {
    // Always fetch fresh data from query params
    const { l4, ln } = this.$route.query
    if (l4 && ln) {
      await this.fetchInitialShifts(l4, ln)
    } else {
      this.$router.replace({ name: 'My Shifts' })
    }
  },
  methods: {
    async selectDay(day) {
      // Guard against duplicate fetches while loading
      if (this.loadingDay) return

      const currentIndex = DAY_ORDER.indexOf(this.selectedDay)
      const newIndex = DAY_ORDER.indexOf(day)
      this.transitionName =
        newIndex > currentIndex ? 'slide-left' : 'slide-right'

      // Skip fetch for tomorrow if before 7pm EDT (we'll show the "check back" message)
      const shouldSkipFetch = day === 'tomorrow' && !this.isAfter7pmEDT

      // If day hasn't been loaded yet, fetch it
      if (this.shifts[day] === null && this.laborerId && !shouldSkipFetch) {
        await this.fetchDayShifts(day)
      }

      this.selectedDay = day
    },

    async fetchInitialShifts(lastFour, lastName) {
      this.isLoading = true
      // Determine which day to fetch based on time
      const initialDay = this.isAfter7pmEDT ? 'tomorrow' : 'today'

      try {
        const response = await lambdaApi.post('laborerShiftLookup', {
          lastFour,
          lastName,
          day: initialDay,
        })

        const data = response.data
        if (data.success) {
          this.laborerId = data.laborer?.id
          this.laborerFirstName = data.laborer?.firstName || ''
          this.shifts[initialDay] = data.shifts || []
          this.selectedDay = initialDay
        } else {
          this.$router.replace({ name: 'My Shifts' })
        }
      } catch (error) {
        console.error('Shift lookup error:', error)
        this.$router.replace({ name: 'My Shifts' })
      } finally {
        this.isLoading = false
      }
    },

    async fetchDayShifts(day) {
      if (!this.laborerId) return

      this.loadingDay = day
      try {
        const response = await lambdaApi.post('laborerShiftLookup', {
          laborerId: this.laborerId,
          day,
        })

        const data = response.data
        if (data.success) {
          this.shifts[day] = data.shifts || []
        } else {
          this.shifts[day] = []
        }
      } catch (error) {
        console.error(`Error fetching ${day} shifts:`, error)
        this.shifts[day] = []
      } finally {
        this.loadingDay = null
      }
    },

    handleReset() {
      this.$router.push({ name: 'My Shifts' })
    },
  },
}
</script>

<style scoped>
.my-shifts-view {
  min-height: 100vh;
  background: linear-gradient(135deg, #5c6b7a 0%, #3d4a5c 100%);
}

.content-area {
  padding-bottom: 24px;
}

.greeting {
  font-size: 1.5rem;
  font-weight: 400;
}

.not-you-link {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  text-decoration: underline;
}

.not-you-link:hover {
  color: white;
}

@media (min-width: 530px) {
  .day-nav {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 8px;
  }
}

/* Slide transitions */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.25s ease-out;
}

.slide-left-enter {
  opacity: 0;
  transform: translateX(30px);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-enter {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
